import {
	Checkbox as MUCheckBox,
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputLabel,
	Stack,
} from "@mui/material";
import { Field, FieldArray } from "formik";
import { Checkbox, CheckboxWithLabel } from "formik-mui";
import React from "react";

import { FieldDataItem, FieldProps } from "./Field";

export interface CheckboxGroupFieldProps extends FieldProps {
	data: FieldDataItem | FieldDataItem[];
	direction?: "row" | "column";
}

const CheckboxGroupField = ({
	direction = "column",
	data,
	className,
	name,
	label,
	id,
	helpText,
	fullWidth = true,
	required,
	...others
}: CheckboxGroupFieldProps) => {
	const isMultiple = Array.isArray(data);
	const realId = id || name;
	const renderField = () => {
		if (isMultiple) {
			return (
				<FieldArray
					name={name}
					render={(arrayHelpers) => {
						const values = (arrayHelpers.form.values[name] as string[]) || [];

						const handleChange =
							(item: FieldDataItem, checked: boolean) =>
							(event: React.ChangeEvent<HTMLInputElement>) => {
								if (event.target.checked) {
									if (!checked) {
										arrayHelpers.push(item.value);
									}
								} else {
									if (checked) {
										const index = values.findIndex((v) => v === item.value);
										if (index >= 0) {
											arrayHelpers.remove(index);
										}
									}
								}
							};

						return (
							<Stack direction="row">
								{data.map((item, index) => {
									const isChecked = values.includes(item.value as string);
									const control = (
										<MUCheckBox
											key={index}
											checked={isChecked}
											value={isChecked}
											onChange={handleChange(item, isChecked)}
										/>
									);
									if (item.label) {
										return (
											<FormControlLabel
												key={index}
												control={control}
												label={item.label}
											/>
										);
									}
									return control;
								})}
							</Stack>
						);
					}}
				/>
			);
		}
		return (
			<Field
				size="small"
				type="checkbox"
				Label={{ label: data.label }}
				id={realId}
				component={data.label ? CheckboxWithLabel : Checkbox}
				name={name}
				aria-describedby={`${realId}-helper-text`}
				{...others}
			/>
		);
	};
	return (
		<FormControl
			fullWidth={fullWidth}
			variant="standard"
			hiddenLabel
			className={className}
			required={required}
		>
			<Stack direction={direction} justifyContent="space-between">
				{label && (
					<InputLabel
						style={{ position: "unset", transform: "none" }}
						disableAnimation
						htmlFor={realId}
						className="text-sm font-semibold"
					>
						{label}
					</InputLabel>
				)}
				{renderField()}
			</Stack>
			{helpText && <FormHelperText id={`${realId}-helper-text`}>{helpText}</FormHelperText>}
		</FormControl>
	);
};

export { CheckboxGroupField };
